// ===== openMeteo.ts — Open-Meteo through the relay first (3.63) =====
//
// Open-Meteo's free tier is limited per IP per day. A phone and a laptop on
// one home IP used the allowance up and Conditions + wind went dead (429
// "Daily API request limit exceeded"). Requests now go to the ais-relay
// (/ais/openmeteo/{forecast,marine}), which answers from a shared cache and
// calls Open-Meteo from the server. Only if the relay fails does the app call
// Open-Meteo directly. The relay accepts the same query string.

const HOST_PATHS: Record<string, string> = {
  'api.open-meteo.com/v1/forecast': 'forecast',
  'marine-api.open-meteo.com/v1/marine': 'marine',
};

// Off under vitest by default (module resets re-run this line); openMeteo.test.ts turns it on.
let relayEnabled = (import.meta as ImportMeta & { env?: { MODE?: string } }).env?.MODE !== 'test';

/** Test hook: the request-budget tests count provider calls, so they run direct-only. */
export function setOpenMeteoRelayEnabled(enabled: boolean): void {
  relayEnabled = enabled;
}

/** Relay base: same-origin <origin>/ais on the web, the okelo.app relay in the native shell. */
export function relayBase(): string | null {
  if (!relayEnabled || typeof window === 'undefined' || !window.location) return null; // node tests: direct only
  const { protocol, hostname, origin } = window.location;
  if (protocol === 'https:' && hostname === 'localhost') return 'https://okelo.app/ais'; // Capacitor
  return `${origin}/ais`;
}

/** Map a direct Open-Meteo URL to its relay URL (null when it has none). */
export function relayUrlFor(directUrl: string, base: string | null = relayBase()): string | null {
  if (!base) return null;
  let u: URL;
  try {
    u = new URL(directUrl);
  } catch {
    return null;
  }
  const kind = HOST_PATHS[u.host + u.pathname];
  if (!kind) return null;
  return `${base}/openmeteo/${kind}${u.search}`;
}

/**
 * fetch() for an Open-Meteo URL: relay first, direct only when the relay
 * fails (network error or any non-OK answer). An aborted request stays
 * aborted — it never falls through to a direct call.
 */
export async function openMeteoFetch(directUrl: string, init?: RequestInit): Promise<Response> {
  const relayUrl = relayUrlFor(directUrl);
  if (relayUrl) {
    try {
      const res = await fetch(relayUrl, init);
      if (res.ok) return res;
    } catch (error) {
      if (init?.signal?.aborted) throw error;
    }
  }
  return fetch(directUrl, init);
}
